import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { refreshPayment, deactivatePaymentLink, getOrderPayableTotal } from './helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { paymentStatus } from './constants';

const canDeactivatePayuPaymentLink = (payment) => {
    return payment?.gatewayName === 'PAYU' &&
        payment?.paymentSource === 'PAYMENT_LINK' &&
        payment?.status === 'PENDING' &&
        typeof payment?.metadata?.invoiceNumber === 'string' &&
        payment.metadata.invoiceNumber.trim() !== '';
}


const PaymentsTable = ({ payments = [], orderTotal = 0, paymentItems = [], isFullyPaid, totalPayments, refetch }) => {
    const [copiedId, setCopiedId] = useState(null);
    const [refreshingPayment, setRefreshingPayment] = useState(false);
    const [deactivatingPaymentId, setDeactivatingPaymentId] = useState(null);

    const displayOrderTotal = getOrderPayableTotal({ paymentItems, orderTotal });

    // Function to get status pill color based on payment status
    const getStatusColor = (status) => {
        switch (status) {
            case 'SUCCESS':
                return 'bg-green-500 text-white-50';
            case 'PENDING':
                return 'bg-yellow-500 text-white-50';
            case 'FAILED':
                return 'bg-red-500 text-white-50';
            case 'CANCELLED':
                return 'bg-gray-500 text-white-50';
            case 'EXPIRED':
                return 'bg-orange-500 text-white-50';
            default:
                return 'bg-gray-400 text-white-50';
        }
    };

    // Function to copy payment link to clipboard
    const copyPaymentLink = async (paymentLink, paymentId) => {
        if (!paymentLink) {
            toast.error('No payment link available');
            return;
        }

        try {
            await navigator.clipboard.writeText(paymentLink);
            setCopiedId(paymentId);
            toast.success('Payment link copied to clipboard');
            // Reset copied state after 2 seconds
            setTimeout(() => {
                setCopiedId(null);
            }, 2000);
        } catch (err) {
            toast.error('Failed to copy payment link');
        }
    };

    // Function to format amount
    const formatAmount = (amount) => {
        return `₹${parseFloat(amount).toFixed(2)}`;
    };


    if (!payments || payments.length === 0) {
        return (
            <div className="bg-white rounded-2xl shadow-soft-xl p-6 mt-4">
                <h3 className="text-lg font-semibold mb-4">Payments</h3>
                <p className="text-gray-500 text-center py-8">No payments found for this order</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl shadow-soft-xl p-6 mt-4 -z-5">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Payments [{payments.length}]</h3>
                {displayOrderTotal > 0 && (
                    <div className="flex items-center gap-4 text-sm">
                        <span>Total Paid: <span className="font-semibold text-green-600">{formatAmount(totalPayments || 0)}</span></span>
                        <span>Order Total: <span className="font-semibold">{formatAmount(displayOrderTotal)}</span></span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${isFullyPaid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                            {isFullyPaid ? '✓ Fully Paid' : 'Payment Pending'}
                        </span>
                    </div>
                )}
            </div>

            {
                refreshingPayment ? <div className="flex items-center gap-2 w-full justify-center">
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Refreshing payment status...
                </div> : <div className="overflow-x-auto">
                    <table className="table-auto w-full">
                        <thead>
                            <tr className="text-left h-12 font-sans border-b-2 border-gray-100">
                                <th className="pl-3 font-sans text-left px-2">Id</th>
                                <th className="pl-3 font-sans text-left px-2">Amount</th>
                                <th className="pl-3 font-sans text-left px-2">Status</th>
                                <th className="pl-3 font-sans text-left px-2">Method</th>
                                <th className="pl-3 font-sans text-left px-2">Source</th>
                                <th className="pl-3 font-sans text-left px-2">Gateway</th>
                                <th className="pl-3 font-sans text-left px-2">Payment Link</th>
                                <th className="pl-3 font-sans text-left px-2">Invoice</th>
                                <th className="pl-3 font-sans text-left px-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="border-collapse">
                            {payments.map((payment, index) => (
                                <tr
                                    key={payment.id || index}
                                    className="text-left border-t border-gray-100 h-14 font-sans hover:bg-gray-50"
                                >
                                    <td className="pl-3 py-4 font-sans text-left">
                                        <span className="font-semibold text-green-600 bg-gray-100 px-2 py-1 rounded">
                                            {payment.id}
                                        </span>
                                    </td>
                                    <td className="pl-3 py-4 font-sans text-left">
                                        <span className="font-semibold text-green-600">
                                            {formatAmount(payment.amount)}
                                        </span>
                                    </td>

                                    <td className="pl-3 py-4 font-sans text-left">
                                        <span
                                            className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}
                                        >
                                            {payment.status}
                                        </span>
                                    </td>

                                    <td className="pl-3 py-4 font-sans text-left">
                                        {payment.paymentMethod || '-'}
                                    </td>

                                    <td className="pl-3 py-4 font-sans text-left">
                                        <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                                            {payment.paymentSource || '-'}
                                        </span>
                                    </td>

                                    <td className="pl-3 py-4 font-sans text-left">
                                        {payment.gatewayName || '-'}
                                    </td>

                                    <td className="pl-3 py-4 font-sans text-left">
                                        {payment.metadata?.paymentLink ? (
                                            <button
                                                onClick={() => copyPaymentLink(payment.metadata.paymentLink, payment.id)}
                                                className={`px-3 py-1 rounded text-xs font-medium transition-colors ${copiedId === payment.id
                                                    ? 'bg-green-100 text-green-800 border border-green-300'
                                                    : 'bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200'
                                                    }`}
                                            >
                                                {copiedId === payment.id ? '✓ Copied' : '📋 Copy Link'}
                                            </button>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </td>

                                    <td className="pl-3 py-4 font-sans text-left">
                                        {payment.metadata?.invoiceNumber ? (
                                            <span className="text-sm font-mono bg-gray-50 px-2 py-1 rounded">
                                                {payment.metadata.invoiceNumber}
                                            </span>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </td>

                                    <td className="pl-3 py-4 font-sans text-left">
                                        <div className="flex flex-wrap items-center gap-2">
                                            {canDeactivatePayuPaymentLink(payment) && (
                                                <button
                                                    type="button"
                                                    onClick={async () => {
                                                        if (
                                                            !window.confirm(
                                                                "This will deactivate the PayU payment link; customers won't be able to pay using this link."
                                                            )
                                                        ) {
                                                            return;
                                                        }
                                                        setDeactivatingPaymentId(payment.id);
                                                        try {
                                                            await deactivatePaymentLink(payment.id, refetch);
                                                        } finally {
                                                            setDeactivatingPaymentId(null);
                                                        }
                                                    }}
                                                    disabled={
                                                        deactivatingPaymentId === payment.id ||
                                                        refreshingPayment ||
                                                        !refetch
                                                    }
                                                    className={`px-2 py-1 rounded text-xs font-medium border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 transition-colors ${deactivatingPaymentId === payment.id || refreshingPayment || !refetch ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    {deactivatingPaymentId === payment.id ? (
                                                        <>
                                                            <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                                                            Deactivating…
                                                        </>
                                                    ) : (
                                                        'Deactivate link'
                                                    )}
                                                </button>
                                            )}
                                            {(payment.status === paymentStatus.PENDING || payment.status === paymentStatus.FAILED || payment.status === paymentStatus.EXPIRED) && (
                                                <button
                                                    type="button"
                                                    onClick={async () => {
                                                        setRefreshingPayment(true);
                                                        await refreshPayment(payment.id, refetch);
                                                        setRefreshingPayment(false);
                                                    }}
                                                    disabled={refreshingPayment || deactivatingPaymentId === payment.id}
                                                    className={`px-2 py-1 border border-gray-300 rounded text-xs bg-gray-50 hover:bg-gray-200 transition-colors ${!refetch ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <FontAwesomeIcon icon={faRefresh} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            }
        </div>
    );
};

PaymentsTable.propTypes = {
    payments: PropTypes.array.isRequired,
    orderTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paymentItems: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    isFullyPaid: PropTypes.bool,
    totalPayments: PropTypes.number,
    refetch: PropTypes.func,
};

export default PaymentsTable; 